import {
  copySection,
  createSection,
  featureBlockSchema,
  type Section,
} from '@/features/storefront/sections/schema';
import {
  hyperDepartments,
  hyperMoreToExplore,
  hyperPromises,
} from '@/previewThemes/hyper/productPage';
import type { SectionPreset } from '@/previewThemes/types';

/**
 * Hyper's own shelf of the "add section" library.
 *
 * A superstore: twelve departments, a wide page, and a shopper who compares
 * nine of a thing side by side before buying one. Its presets say that - the
 * grids are denser, the bands are about range and reassurance rather than
 * about a story, and the merchandising sits where this design puts it.
 *
 * They follow the same two rules as every theme's presets: no photography from
 * the demo shoot, and placeholder copy that prompts rather than claims. See
 * `previewThemes/feast/presets.ts` for why.
 */

/** Anywhere a shopper is browsing rather than checking out. */
const BROWSING = ['home', 'listing', 'category', 'search'] as const;

/** The one page the bands below were drawn for. */
const PRODUCT = ['product'] as const;

function build(section: Section): Section {
  return section;
}

/**
 * One of the product page's own bands, ready to be added again.
 *
 * The product page ships as a stack of these - see `productPage.ts` - and a
 * merchant who deletes a band has to be able to get it back as it was rather
 * than as an empty section they refill by hand. So the preset and the default
 * are the same builder, re-identified: `copySection` gives the section and
 * every block in it a fresh id, because two merchants adding the same band
 * must not share one.
 */
function band(section: Section): Section {
  return copySection(section);
}

export const HYPER_PRESETS: readonly SectionPreset[] = [
  // --- The product page's own bands ---------------------------------------
  // The same three the page ships with - see `productPage.ts` - and, like
  // them, drawn from the merchant's own catalogue or worded so that any shop
  // can stand behind them. No photographs or quotes from the demo shoot.
  {
    id: 'product-more-to-explore',
    label: 'More to explore',
    description: 'A dense shelf of your newest products under the one being viewed.',
    type: 'featured_products',
    pages: PRODUCT,
    build: () => band(hyperMoreToExplore()),
  },
  {
    id: 'product-departments',
    label: 'Departments',
    description: 'Your categories on the theme’s own pale ground.',
    type: 'categories',
    pages: PRODUCT,
    build: () => band(hyperDepartments()),
  },
  {
    id: 'product-promises',
    label: 'Shop with confidence',
    description: 'Checkout, order updates and help, at the foot of the page.',
    type: 'features',
    pages: PRODUCT,
    build: () => band(hyperPromises()),
  },

  {
    id: 'department-rail',
    label: 'Department rail',
    description: 'Every department at once, six across, the way the masthead lists them.',
    type: 'categories',
    pages: BROWSING,
    build: () =>
      build({
        ...createSection('categories'),
        title: 'Shop by department',
        columns: 6,
      }),
  },
  {
    id: 'compare-the-range',
    label: 'Compare the range',
    description: 'A wide grid of one department, for a shopper choosing between them.',
    type: 'featured_products',
    pages: BROWSING,
    build: () =>
      build({
        ...createSection('featured_products'),
        title: 'Compare the range',
        subtitle: 'Pin this to a department in the settings on the right.',
        limit: 8,
        columns: 4,
      }),
  },
  {
    id: 'deal-of-the-day',
    label: 'Deal of the day',
    description: 'One line across the page, in the brand colour.',
    type: 'banner',
    build: () =>
      build({
        ...createSection('banner'),
        text: 'Today only - write the offer and when it ends',
        background: 'primary',
        padding: 'sm',
      }),
  },
  {
    id: 'room-wall',
    label: 'Room wall',
    description: 'Photographs of the range in a room, each one a link into it.',
    type: 'gallery',
    build: () =>
      build({
        ...createSection('gallery'),
        title: 'In the room',
        columns: 4,
        padding: 'lg',
      }),
  },
  {
    id: 'delivery-and-assembly',
    label: 'Delivery and assembly',
    description: 'The four promises a big-ticket shopper is looking for.',
    type: 'features',
    build: () =>
      build({
        ...createSection('features'),
        title: '',
        columns: 4,
        background: 'surface',
        blocks: [
          featureBlockSchema.parse({ icon: 'truck', title: 'Delivered to the room' }),
          featureBlockSchema.parse({ icon: 'package', title: 'Assembly included' }),
          featureBlockSchema.parse({ icon: 'return', title: 'Thirty days to change your mind' }),
          featureBlockSchema.parse({ icon: 'shield', title: 'Five-year guarantee' }),
        ],
      }),
  },
  {
    id: 'showroom-story',
    label: 'Showroom story',
    description: 'A picture beside what the shop is and where it came from.',
    type: 'image_with_text',
    build: () =>
      build({
        ...createSection('image_with_text'),
        heading: 'Our showroom',
        body: 'What a shopper would see if they walked in, and why it is worth the trip.',
        image_side: 'left',
        padding: 'lg',
      }),
  },
  {
    id: 'buying-questions',
    label: 'Buying questions',
    description: 'Delivery windows, returns on flat-pack, what fits through a door.',
    type: 'faq',
    build: () =>
      build({
        ...createSection('faq'),
        title: 'Before you order',
      }),
  },
  {
    id: 'customer-photos',
    label: 'Customer photos',
    description: 'What the range looks like in somebody else’s house.',
    type: 'gallery',
    build: () =>
      build({
        ...createSection('gallery'),
        title: 'From our customers',
        columns: 4,
        background: 'surface',
      }),
  },
  {
    id: 'rated-by-shoppers',
    label: 'Rated by shoppers',
    description: 'Three reviews with their star ratings.',
    type: 'testimonials',
    build: () =>
      build({
        ...createSection('testimonials'),
        title: 'Rated by shoppers',
        columns: 3,
      }),
  },
];
